import React, { useState, useEffect } from 'react';
import Header from '../../components/admin/Header';
import Sidebar from '../../components/admin/Sidebar';
import Footer from '../../components/admin/Footer';
import { 
  Upload,
  Trash2,
  Image as ImageIcon,
  X,
  Eye,
  Plus,
  AlertCircle,
  Calendar,
  FileText,
  Download,
  Copy,
  ExternalLink
} from 'lucide-react';
import axios from 'axios';

const UploadLogo = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState('');
  const [logos, setLogos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    imageUrl: ''
  });

  // Fetch logos
  const fetchLogos = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get('https://api.marketmaestroovip.in/api/logo');
      setLogos(res.data);
    } catch (err) {
      console.error('Error fetching logos:', err);
      setLogos([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLogos();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setImage(file);
    if (file) {
      setPreview(URL.createObjectURL(file));
    }
    setMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!image) {
      setMessage('Please select an image first');
      return;
    }

    const formData = new FormData();
    formData.append('image', image);

    try {
      const res = await axios.post(
        'https://api.marketmaestroovip.in/api/logo/upload',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setMessage('Logo uploaded successfully!');
      setImage(null);
      setPreview(null);
      fetchLogos();
      
      // Clear file input
      const fileInput = document.getElementById('logo-upload');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Upload error:', error);
      setMessage('Upload failed. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this logo?')) return;

    try {
      await axios.delete(`https://api.marketmaestroovip.in/api/logo/${id}`);
      fetchLogos();
    } catch (error) {
      console.error('Delete error:', error);
      alert('Delete failed. Please try again.');
    }
  };

  const openPreviewModal = (imageUrl) => {
    setPreviewModal({
      isOpen: true,
      imageUrl
    });
  };

  const closePreviewModal = () => {
    setPreviewModal({
      isOpen: false,
      imageUrl: ''
    });
  };

  const removePreview = () => {
    setImage(null);
    setPreview(null);
    setMessage('');
    const fileInput = document.getElementById('logo-upload');
    if (fileInput) fileInput.value = '';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' Bytes';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="flex h-screen">
        {/* Sidebar */}
        <Sidebar isOpen={sidebarOpen} />
        
        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-h-screen overflow-hidden lg:ml-64">
          {/* Header */}
          <Header toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />

          {/* Main Content */}
          <main className="flex-1 overflow-y-auto p-4 md:p-6">
            {/* Page Header */}
            <div className="mb-6 md:mb-8">
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                Logo Management
              </h1>
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mt-1 md:mt-2">
                Upload and manage your logos
              </p>
            </div>

            {/* Upload Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6 mb-6">
              <h2 className="text-lg md:text-xl font-semibold mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Upload New Logo
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* File Upload Area */}
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 md:p-8 text-center hover:border-blue-500 transition-colors">
                  <input
                    type="file"
                    id="logo-upload"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <label htmlFor="logo-upload" className="cursor-pointer block">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mb-4">
                        {preview ? (
                          <ImageIcon className="w-8 h-8 text-blue-600 dark:text-blue-300" />
                        ) : (
                          <Plus className="w-8 h-8 text-blue-600 dark:text-blue-300" />
                        )}
                      </div>
                      <p className="text-lg font-medium mb-2">
                        {preview ? 'Change Logo' : 'Click to select logo'}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        PNG, JPG, JPEG, GIF, SVG files up to 5MB
                      </p>
                      <div className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition duration-200 inline-block">
                        {preview ? 'Change File' : 'Select File'}
                      </div>
                    </div>
                  </label>
                </div>

                {/* Preview Section */}
                {preview && (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium flex items-center gap-2">
                        <ImageIcon className="w-4 h-4" />
                        Preview
                      </h3>
                      <button
                        type="button"
                        onClick={removePreview}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex justify-center">
                      <img
                        src={preview}
                        alt="Preview"
                        className="max-h-48 max-w-full object-contain rounded"
                      />
                    </div>
                  </div>
                )}

                {/* Upload Button */}
                <div className="flex items-center gap-4">
                  <button
                    type="submit"
                    disabled={!image}
                    className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition duration-200 ${
                      image 
                        ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                        : 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <Upload className="w-5 h-5" />
                    Upload Logo
                  </button>

                  {/* Message */}
                  {message && (
                    <div className={`p-3 rounded-lg flex items-center gap-2 ${
                      message.includes('success') 
                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                        : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                    }`}>
                      <AlertCircle className="w-5 h-5" />
                      <span>{message}</span>
                    </div>
                  )}
                </div>
              </form>
            </div>

            {/* Logos List Section - TABLE FORMAT */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg md:text-xl font-semibold flex items-center gap-2">
                  <ImageIcon className="w-5 h-5" />
                  Uploaded Logos
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                    ({logos.length})
                  </span>
                </h2>
                <button
                  onClick={fetchLogos}
                  className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg flex items-center gap-2"
                >
                  <span className="rotate-90">↻</span>
                  Refresh
                </button>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : logos.length === 0 ? (
                <div className="text-center py-12">
                  <ImageIcon className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                  <h3 className="text-lg font-medium mb-2">No logos uploaded yet</h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Upload your first logo using the upload section above
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Preview
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Logo Details
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          File Info
                        </th>
                        {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Uploaded
                        </th> */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {logos.map((logo) => (
                        <tr key={logo.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                          {/* Preview Column */}
                          <td className="px-4 py-4 whitespace-nowrap">
                            <button
                              onClick={() => openPreviewModal(`https://api.marketmaestroovip.in/${logo.image}`)}
                              className="w-16 h-16 flex items-center justify-center bg-gray-100 dark:bg-gray-600 rounded-lg overflow-hidden hover:opacity-90 transition"
                            >
                              <img
                                src={`https://api.marketmaestroovip.in/${logo.image}`}
                                alt={logo.name || 'Logo'}
                                className="w-full h-full object-contain"
                                onError={(e) => {
                                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIGZpbGw9IiNFNkU2RTYiLz48cGF0aCBkPSJNMzEgMjJIMjdWMThIMjFWMjJIMTdWMjhIMjFWMzJIMjdWMjhIMzFWMjJaIiBmaWxsPSIjOTk5OTk5Ii8+PC9zdmc+';
                                }}
                              />
                            </button>
                          </td>

                          {/* Logo Details Column */}
                          <td className="px-4 py-4">
                            <div>
                              
                              <div className="mt-2">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    readOnly
                                    value={`https://api.marketmaestroovip.in/${logo.image}`}
                                    className="w-100 px-3 py-1 text-xs bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded truncate"
                                    onClick={(e) => e.target.select()}
                                  />
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(`https://api.marketmaestroovip.in/${logo.image}`);
                                      alert('URL copied to clipboard!');
                                    }}
                                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                    title="Copy URL"
                                  >
                                    <Copy className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* File Info Column */}
                          <td className="px-4 py-4">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-gray-400" />
                                <span className="text-xs">
                                  Type: {logo.mime_type || 'image/*'}
                                </span>
                              </div>
                              
                            </div>
                          </td>

                          {/* Uploaded Date Column */}
                          {/* <td className="px-4 py-4">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              <span className="text-xs">
                                {formatDate(logo.created_at)}
                              </span>
                            </div>
                          </td> */}

                          {/* Actions Column */}
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openPreviewModal(`https://api.marketmaestroovip.in/${logo.image}`)}
                                className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900 rounded-lg transition"
                                title="Preview"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => window.open(`https://api.marketmaestroovip.in/${logo.image}`, '_blank')}
                                className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900 rounded-lg transition"
                                title="Open in New Tab"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(logo.id)}
                                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900 rounded-lg transition"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </main>

          {/* Footer */}
          <Footer />
        </div>
      </div>

      {/* Preview Modal */}
      {previewModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold">Logo Preview</h3>
              <button
                onClick={closePreviewModal}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 p-4 overflow-auto flex items-center justify-center">
              <img 
                src={previewModal.imageUrl} 
                alt="Preview" 
                className="max-h-full max-w-full object-contain"
              />
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => window.open(previewModal.imageUrl, '_blank')}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition"
              >
                <ExternalLink className="w-4 h-4" />
                Open in New Tab
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(previewModal.imageUrl);
                  alert('URL copied to clipboard!');
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 transition"
              >
                <Copy className="w-4 h-4" />
                Copy URL
              </button>
              <button
                onClick={closePreviewModal}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadLogo;